"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { cardVariants } from "@/lib/animations";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";

export interface OverviewAccordionItem {
    /** Bölüm anchor id'si (menü #link uyumu için korunur) */
    sectionId: string;
    title: string;
    /** Zengin metin (HTML); sanitize edilerek render edilir */
    contentHtml: string;
}

type OverviewDefaultExpanded = 'none' | 'first' | 'all';

interface OverviewAccordionProps {
    items: OverviewAccordionItem[];
    /** İlk yüklemede hangi paneller açık olsun (varsayılan: hepsi) */
    defaultExpanded?: OverviewDefaultExpanded;
    /** @deprecated `defaultExpanded="first"` kullanın */
    defaultFirstOpen?: boolean;
}

function initialOpenKeys(
    items: OverviewAccordionItem[],
    mode: OverviewDefaultExpanded,
): Set<string> {
    if (!items.length || mode === 'none') {
        return new Set();
    }
    if (mode === 'first') {
        return new Set([items[0].sectionId]);
    }
    return new Set(items.map((item) => item.sectionId));
}

/**
 * Overview ("Diğer Açıklamalar" + tour_static) içerikleri için akordiyon.
 * Her öğe kendi `id`'sini korur; böylece menüdeki `#sectionId` bağlantıları çalışmaya devam eder.
 * Stil PriceDetailTable akordiyonu ile tutarlıdır (koyu başlık, sağda chevron).
 */
export default function OverviewAccordion({
    items,
    defaultExpanded = 'all',
    defaultFirstOpen,
}: OverviewAccordionProps) {
    const expandedMode: OverviewDefaultExpanded =
        defaultFirstOpen === true ? 'first' : defaultExpanded;

    const [openKeys, setOpenKeys] = useState<Set<string>>(() =>
        initialOpenKeys(items, expandedMode),
    );

    if (!items || items.length === 0) {
        return null;
    }

    const toggle = (key: string) => {
        setOpenKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <div className="space-y-4">
            {items.map((item) => {
                const isOpen = openKeys.has(item.sectionId);
                const panelId = `overview-panel-${item.sectionId}`;
                return (
                    <motion.div
                        key={item.sectionId}
                        id={item.sectionId}
                        variants={cardVariants}
                        className="w-full scroll-mt-24 border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                        <button
                            type="button"
                            onClick={() => toggle(item.sectionId)}
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            className="group relative w-full bg-alsero-dark-gray px-4 py-4 pr-12 text-left transition-all hover:bg-opacity-90 sm:px-6 sm:pr-14"
                        >
                            <span className="block min-w-0 pr-2 text-base font-semibold leading-snug text-white break-words md:text-lg">
                                {item.title}
                            </span>
                            <motion.span
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white sm:right-4"
                                aria-hidden
                            >
                                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </motion.span>
                        </button>

                        <AnimatePresence initial={false}>
                            {isOpen && (
                                <motion.div
                                    id={panelId}
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                    className="overflow-hidden"
                                >
                                    <div
                                        className="border-t border-gray-200 bg-white px-4 py-5 text-alsero-text-tour leading-relaxed prose prose-lg max-w-none sm:px-6"
                                        dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(item.contentHtml) }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                );
            })}
        </div>
    );
}
